import { IrrigationSystem } from "@/types/irrigationSystem";
import { FertilizerProduct } from "@/types/products";
import {
    object,
    date,
    number,
    string,
    array,
    boolean,
    InferType,
    mixed,
} from "yup";
import {
    depthToSpeed,
    productRateToTargetRateN,
    speedToDepth,
    targetRateNToProdutcRate,
} from "../utils/operations";

export const fileInputSchema = array().of(
    object().shape({
        required: boolean().nullable(),
        file_label: string().when("required", {
            is: (required: boolean) => required,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
        unit: string().when("required", {
            is: (required: boolean) => required,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
        label: string().when("required", {
            is: (required: boolean) => required,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
        unit_options: array().of(string()),
    }),
);

export const operationFormSchema = object().shape({
    start_date: date().required().label("Start Date"),
    prescription: number().nullable(),
    application_method: string()
        .oneOf(["fertigation", "nonfertigation"])
        .required()
        .label("Method"),
    product: number().required().label("Product"),
    is_complete: boolean().required().label("Status"),
    notes: string().nullable().label("Notes"),
    technique: string()
        .label("Technique")
        .when("application_method", {
            is: (application_method: string) =>
                application_method === "nonfertigation",
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
    batch_fill: mixed(),
    pump: number()
        .label("Pump")
        .when("application_method", {
            is: (application_method: string) =>
                application_method === "fertigation",
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
    template: number().nullable().notRequired(),
    properties: fileInputSchema,
    table: array().when("application_method", {
        is: (application_method: unknown) =>
            application_method === "fertigation",
        then: () =>
            array().of(
                object().shape({
                    target_rate_n: number()
                        .min(0)
                        .required("Required")
                        .label("Target Rate")
                        .typeError("Required"),
                    depth: number()
                        .typeError("Required")
                        .min(0)
                        .required("Required")
                        .label("Depth"),
                    product_rate: number()
                        .min(0)
                        .typeError("Required")
                        .required("Required")
                        .label("Product Rate"),
                }),
            ),
        otherwise: () =>
            array().of(
                object().shape({
                    target_rate_n: number()
                        .min(0)
                        .typeError("Required++")
                        .required("Required")
                        .label("Target Rate"),
                    product_rate: number()
                        .min(0)
                        .typeError("Required++")
                        .required("Required")
                        .label("Target Rate"),
                }),
            ),
    }),
});

export function degreesError({
    table,
    application_method,
    irrigationSystem,
    hasSectors,
}: {
    table: OperationFormType["table"];
    application_method: OperationFormType["application_method"];
    irrigationSystem?: IrrigationSystem;
    hasSectors: boolean;
}) {
    if (
        application_method !== "fertigation" ||
        !irrigationSystem ||
        hasSectors ||
        !table
    ) {
        return undefined;
    }

    const pStart = irrigationSystem.pivot_start_degree;
    const pStop = irrigationSystem.pivot_stop_degree;

    function getRange(start: number, stop: number): number {
        return stop < start ? 360 - start + stop : stop - start;
    }

    let totalDegrees = 0;
    for (let i = 0; i < table.length; i++) {
        const sector = table[i];
        if (sector.start === undefined || sector.stop === undefined) {
            return "Degrees start/stop are required"; // Missing start/stop
        }
        const lastStop = table[i - 1]?.stop ?? 0;
        if (sector.start < lastStop) {
            return "Degrees start/stop are overlapping"; // Overlapping detected
        }
        totalDegrees =
            totalDegrees + getRange(Number(sector.start), Number(sector.stop));
        if (i > 0 && sector.start !== lastStop) {
            return "Degrees are not continuous"; // Discontinuity detected
        }
    }
    if (totalDegrees !== getRange(pStart, pStop)) {
        return `Degrees do not match irrigation system start/stop of ${pStart}/${pStop}`; // Total degrees do not sum to total
    }

    return undefined;
}

export type OperationFormType = InferType<typeof operationFormSchema>;

export function autoPopulateTable(
    table?: {
        target_rate_n?: number;
        product_rate?: number;
        speed?: number;
        depth?: number;
    }[],
    selectedProduct?: FertilizerProduct,
    irrigationSystem?: IrrigationSystem,
) {
    if (!table) return [];
    return table.map((feature) => {
        if (!selectedProduct) return feature;

        let formattedFeature = {
            ...feature,
        };

        if (feature.target_rate_n) {
            formattedFeature = {
                ...formattedFeature,
                product_rate: targetRateNToProdutcRate(
                    feature.target_rate_n,
                    selectedProduct,
                ),
            };
        }

        if (feature.speed) {
            formattedFeature = {
                ...formattedFeature,
                depth: speedToDepth(feature.speed, irrigationSystem),
            };
        }

        if (!feature.target_rate_n && feature.product_rate) {
            formattedFeature = {
                ...formattedFeature,
                target_rate_n: productRateToTargetRateN(
                    feature.product_rate,
                    selectedProduct,
                ),
            };
        }
        if (!feature.speed && feature.depth) {
            formattedFeature = {
                ...formattedFeature,
                speed: depthToSpeed(feature.depth, irrigationSystem),
            };
        }
        return formattedFeature;
    });
}
